<template>
  <div class="container px-2 align-self-center">
    <div class="row">
      <div class="col text-center">
        <h1 class="mb-1">{{ t('Welcome to IntelliKid Systems') }}</h1>
        <p class="mb-5">{{
          t(
            isDomainMigrated
              ? `Use the button below to log in to IntelliKid Systems and update your bookmark to https://${migrationDomain}`
              : 'Use the form below to log in to your IntelliKid Systems account.',
          )
        }}</p>
      </div>
    </div>
    <div v-if="isDomainMigrated" class="row justify-content-center">
      <div class="col-12 col-sm-8 col-md-6 col-lg-4 col-xl-3">
        <a :href="redirectUrl" class="btn btn-outline-primary rounded-pill w-100"
          >{{ t('Go to Login page') }}
        </a>
      </div>
    </div>
    <template v-else>
      <div class="row">
        <div class="col-md-7 col-lg-5 col-xl-4 m-auto">
          <form @submit="onSubmit">
            <div v-if="loginFailedMessage" class="mb-3 text-danger text-center">
              {{ t(loginFailedMessage) }}
              <RouterLink
                v-if="showResetPasswordLink"
                class="btn-link text-danger"
                :to="{ name: RouteList.AUTH.PASSWORD.FORGOT_PASSWORD.NAME }"
              >
                {{ t('Please change your password.') }}
              </RouterLink>
            </div>
            <div class="mb-3">
              <base-email-input
                v-model="username"
                :placeholder="t('Email')"
                :class="{ 'is-invalid': validationErrors.username }"
                autocomplete="username"
              />
              <div class="invalid-feedback">
                {{ validationErrors.username }}
              </div>
            </div>
            <div class="mb-3">
              <base-password-input
                v-model="password"
                :placeholder="t('Password')"
                :class="{ 'is-invalid': validationErrors.password }"
              />
              <div class="invalid-feedback">
                {{ validationErrors.password }}
              </div>
            </div>
            <div class="d-grid">
              <BaseSubmitButton
                class="btn btn-primary rounded-pill"
                :disabled="Object.values(socialAuthLoadState).some(Boolean)"
                :is-submitting="isSubmitting"
                :text="t('Login')"
              />
            </div>
          </form>
        </div>
      </div>
      <div class="row mb-3 mt-3">
        <div class="col-md-7 col-lg-5 col-xl-4 m-auto">
          <RouterLink
            class="text-muted btn-link"
            :to="{ name: RouteList.AUTH.PASSWORD.FORGOT_PASSWORD.NAME }"
          >
            {{ t('Forgot Password?') }}
          </RouterLink>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-md-7 col-lg-5 col-xl-4 m-auto">
          <div class="text-center"> OR </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-7 col-lg-5 col-xl-4 m-auto">
          <div>
            <BaseSubmitButton
              :is-submitting="socialAuthLoadState[SocialAuthEnum.GOOGLE]"
              :disabled="isSubmitting || socialAuthLoadState[SocialAuthEnum.MICROSOFT]"
              type="button"
              btn-class="btn btn-outline-primary rounded-pill d-flex align-items-center py-2 px-3 text-decoration-none justify-content-center mb-3 w-100"
              @click.prevent="socialAuth(SocialAuthEnum.GOOGLE)"
            >
              <BasePortalIcon icon="google-color" class="me-2 fs-3" />
              {{ t('Login with Google') }}
            </BaseSubmitButton>
            <BaseSubmitButton
              :is-submitting="socialAuthLoadState[SocialAuthEnum.MICROSOFT]"
              :disabled="isSubmitting || socialAuthLoadState[SocialAuthEnum.GOOGLE]"
              type="button"
              btn-class="btn btn-outline-primary rounded-pill d-flex align-items-center py-2 px-3 text-decoration-none justify-content-center mb-3 w-100"
              @click.prevent="socialAuth(SocialAuthEnum.MICROSOFT)"
            >
              <BasePortalIcon icon="microsoft-color" class="me-2 fs-3" />
              {{ t('Login with Microsoft') }}
            </BaseSubmitButton>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { computed, reactive, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useForm, useField } from 'vee-validate'
import { string, object, SchemaOf } from 'yup'
import { isAxiosError } from 'axios'
import { useAuth } from '@/store/Auth'
import { RouteList } from '@/routes/RouteList'
import useSocial from '@/composables/useSocial'
import { SocialAuthEnum } from '@/enums/SocailEnum'
import BaseSubmitButton from '@/components/BaseComponents/Forms/BaseSubmitButton.vue'
import { HttpStatusCodes } from '@/enums/HttpStatusCodes'

type LoginCredentials = {
  username: string
  password: string
}

const authStore = useAuth()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { socialOauth } = useSocial()

const socialAuthLoadState = reactive({
  [SocialAuthEnum.GOOGLE]: false,
  [SocialAuthEnum.MICROSOFT]: false,
})

const isDomainMigrated = import.meta.env.VITE_DOMAIN_MIGRATED ?? false
const migrationDomain = import.meta.env.VITE_MIGRATION_DOMAIN ?? 'app.intellikidsystems.com'

const redirectUrl = computed(() => {
  if (route.query.redirect) {
    return `https://${migrationDomain}/#/login?redirect=${route.query.redirect}`
  }

  return `https://${migrationDomain}/#/login`
})

const validationSchema: SchemaOf<LoginCredentials> = object({
  username: string().required().email().label(t('Email')).defined(),
  password: string().required().label(t('Password')).defined(),
})

const {
  errors: validationErrors,
  handleSubmit,
  isSubmitting,
} = useForm({
  initialValues: <LoginCredentials>{
    username: '',
    password: '',
  },
  validationSchema,
})

const { value: username } = useField('username')
const { value: password } = useField('password')

const loginFailedMessage = ref<string>()
const showResetPasswordLink = ref(false)

const onSubmit = handleSubmit(async (payload: { username: string; password: string }) => {
  loginFailedMessage.value = undefined
  showResetPasswordLink.value = false

  try {
    await authStore.actions.authenticateUsingPassword(payload)

    if (route.query.redirect) {
      await router.replace({
        path: route.query.redirect.toString(),
      })
    } else {
      await router.replace({
        name: RouteList.AUTH.BUSINESS_CHOICE.NAME,
      })
    }
  } catch (e) {
    if (isAxiosError(e)) {
      loginFailedMessage.value = e.response?.data?.message

      if (e.response?.status === HttpStatusCodes.CredentialsExpired) {
        loginFailedMessage.value = 'Your password has expired!'
        showResetPasswordLink.value = true
      }
    }
  }
})

const socialAuth = async (provider: SocialAuthEnum) => {
  loginFailedMessage.value = undefined
  socialAuthLoadState[provider] = true

  const { code } = await socialOauth(provider)

  try {
    await authStore.actions.authenticateUsingSocial({ provider, code })

    if (route.query.redirect) {
      await router.replace({
        path: route.query.redirect.toString(),
      })
    } else {
      await router.replace({
        name: RouteList.AUTH.BUSINESS_CHOICE.NAME,
      })
    }
  } catch (e) {
    if (isAxiosError(e)) {
      loginFailedMessage.value = e.response?.data?.message
    }
  } finally {
    socialAuthLoadState[provider] = false
  }
}
</script>
